import { Injectable } from '@angular/core';
import { AbstractSandbox } from 'app/core/sandboxes/abstract.sandbox';
import { ActiveCustomerService } from 'app/core/services/active-customer.service';
import { Customer } from 'app/core/store/session/user-context/user-context.interface';
import { TemplatesLoadAction } from 'app/core/store/template/template.actions';
import { goAction } from '../store/router/router.actions';
import { distinctUntilChanged, map, withLatestFrom } from 'rxjs/operators';
import { FlordersFilter } from 'app/core/interfaces/florders-filter.interface';
import { CreateOrderModalData } from 'app/core/store/calendar/orders/calendar-orders.interface';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { CalendarCreateOrderModalComponent } from 'app/modules/calendar/orders/calendar-create-order-modal/calendar-create-order-modal.component';
import { mapOrdersTo2Orders } from 'app/core/store/orders/orders.reducer';
import { OrderRestService } from 'app/core/services/rest/order.rest.service';
import { Order } from '../interfaces/order.interface';
import { getFirst$$ } from 'app/core/util/util';
import { CustomerInfoService } from '../services/customer-info.service';
import { firstValueFrom } from 'rxjs';
import { selectOrderDepots } from '../store/calendar/orders/calendar-orders.selectors';

@Injectable({ providedIn: 'root' })
export class OrdersCalendarSandbox extends AbstractSandbox {
  isSuperUser = false;
  allowedTemplateUsers: Customer[] = [];

  definitions$ = this.store.select(state => state.definitions).pipe(distinctUntilChanged());
  places$ = this.store.select(state => state.customerInfo.places).pipe(distinctUntilChanged());
  templateState$ = this.store.select(state => state.template).pipe(distinctUntilChanged());
  depots$ = this.store.select(selectOrderDepots);

  customers$ = this.store
    .select(state => ({
      selectedCustomer: state.session.selectedCustomer.data,
      isSuperUser: state.session.userContext.isSuperUser,
      customersForLegend: state.session.customerPermissions.data.calenderLegend,
    }))
    .pipe(
      map(({ selectedCustomer, isSuperUser, customersForLegend }) => {
        this.isSuperUser = isSuperUser;
        if (isSuperUser) {
          return customersForLegend;
        } else {
          return [selectedCustomer];
        }
      }),
      distinctUntilChanged()
    );

  calendarCustomers$ = this.store
    .select(state => state.calendar.orders.data.legend)
    .pipe(
      withLatestFrom(this.customers$, (calendarCustomers, customers) => {
        const result = customers.map(customer => {
          const calendarCustomer = calendarCustomers.find(cc => cc.id === customer.id);
          return {
            ...customer,
            amount: (calendarCustomer && calendarCustomer.amount) || '',
          };
        });
        result.sort((c1, c2) => (c1.name < c2.name ? -1 : 1));
        return result;
      }),
      distinctUntilChanged()
    );

  /**
   * Can view templates if:
   * - is superuser and has correct customerPermissions (only loaded if superuser = transporter = multiple custoners user)
   * - is not superuser and has authorization
   */
  canViewTemplates$ = this.store
    .select(state => ({
      activeCustomer: state.session._activeCustomer.data,
      allowedTemplateUsers: state.session.customerPermissions.data.createOrUpdateOrder,
      isTransporter: state.session.userContext?.isSuperUser,
      canCreateOrders: state.session.authorization.data?.ORDER.CREATE,
    }))
    .pipe(
      map(({ activeCustomer, allowedTemplateUsers, isTransporter, canCreateOrders }) => {
        if (isTransporter) {
          this.allowedTemplateUsers = allowedTemplateUsers;
          return !!this.checkAllowedCustomers(activeCustomer, allowedTemplateUsers);
        } else {
          return canCreateOrders;
        }
      }),
      distinctUntilChanged()
    );

  constructor(private activeCustomerService: ActiveCustomerService, private dialog: MatDialog, private orderRestService: OrderRestService, private customerInfoService: CustomerInfoService) {
    super();
  }

  checkAllowedCustomers(user: Customer, customers: Customer[]) {
    if (user && customers) {
      return !!customers.find(c => {
        return c.id === user.id;
      });
    }
  }

  async loadTemplates(customer?: Customer) {
    if (customer) {
      await this.activeCustomerService.setActiveCustomer(customer);
    }
    const hasAllowedCustomers = this.checkAllowedCustomers(customer, this.allowedTemplateUsers);
    if (customer && (!this.isSuperUser || (this.isSuperUser && hasAllowedCustomers))) {
      this.store.dispatch(new TemplatesLoadAction({ type: 'ORDER' }));
    }
  }

  async createOrderFromTemplate(customer: Customer, templateId: number, date: string) {
    await this.activeCustomerService.setActiveCustomer(customer);
    if (this.isSuperUser) {
      await firstValueFrom(this.customerInfoService.loadCustomerInfo(customer, true));
    }
    this.store.dispatch(
      goAction({
        path: [`orders/new/fromTemplate/${templateId}`],
        query: { date },
      })
    );
  }

  async showCreateOrderModal(data: CreateOrderModalData) {
    const config: MatDialogConfig = {
      width: '75%',
      height: 'auto',
      data,
      role: 'dialog',
    };

    const dialogRef = this.dialog.open(CalendarCreateOrderModalComponent, config);
    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        if (result.createOrder) {
          if (result.id) {
            // start from template
            if (this.isSuperUser) {
              await firstValueFrom(this.customerInfoService.loadCustomerInfo(data.activeCustomer, true));
            }
            this.store.dispatch(
              goAction({
                path: [`orders/new/fromTemplate/${result.id}`],
                query: { date: result.date },
              })
            );
          } else {
            // start from scratch
            this.store.dispatch(
              goAction({
                path: [`orders/new`],
                query: { date: result.date },
              })
            );
          }
        } else if (result.id) {
          // navigate to existing order
          this.store.dispatch(
            goAction({
              path: [`orders/${result.id}`],
              query: {
                salesOrderNumber: result.salesOrderNumber,
                back: 'calendar/order',
              },
            })
          );
        }
      }
    });
  }

  async loadOrders$$(dayFilter: FlordersFilter): Promise<Order[]> {
    const ordersTos = await getFirst$$(this.orderRestService.getOrders(dayFilter));
    const orders = mapOrdersTo2Orders({
      orders: ordersTos,
      definitions: await getFirst$$(this.definitions$),
      places: await getFirst$$(this.places$),
    });
    return orders;
  }
}
